import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Icon } from '../icons';
import Checkbox from './Checkbox';
import Popover from './Popover';
import { IconButton } from './Button';
import { useT } from '../../i18n';

/**
 * DataTable — compact, resizable, sortable table primitive.
 *
 * Columns:
 *   { key, header, width?, minWidth?=80, align?='left',
 *     sortable?=false, resizable?=true, filterable?=false,
 *     sortAccessor?(row), render?(row, i) }
 *
 * Props:
 *   columns, data
 *   rowKey:(row, i) => string|number      — default i
 *   sort / onSortChange                    — optional controlled sort {key, direction}
 *   manualSort: boolean                    — if true, do not sort internally
 *   selectable: boolean
 *   selectedKeys: Array | Set, onSelectionChange(nextArray)
 *   density: 'compact' | 'comfortable'     — default 'comfortable'
 *   stickyHeader: boolean                  — default true
 *   onRowClick:(row, i) => void
 *   emptyState: ReactNode
 *   renderColumnFilter:(col, { close }) => ReactNode
 *       — when set, columns with `filterable: true` show a funnel in the header
 *         that opens a popover containing sort options + this editor.
 *   columnFilterActiveKeys: Array|Set       — keys whose funnel should appear active
 *   rowClassName: string | (row, i) => string  — extra class(es) per row
 */
const DataTable = ({
  columns = [],
  data = [],
  rowKey = (_row, i) => i,
  sort,
  onSortChange,
  manualSort = false,
  selectable = false,
  selectedKeys,
  onSelectionChange,
  density = 'comfortable',
  stickyHeader = true,
  onRowClick,
  emptyState = null,
  renderColumnFilter,
  columnFilterActiveKeys,
  rowClassName,
  className = '',
}) => {
  const t = useT();
  // ---- column widths (resizable) -------------------------------------------
  const [widths, setWidths] = useState(() => {
    const init = {};
    columns.forEach((c) => { init[c.key] = c.width || 160; });
    return init;
  });
  const dragRef = useRef(null);

  const onResizeStart = useCallback((e, col) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startW = widths[col.key] || col.width || 160;
    const minW = col.minWidth || 80;
    dragRef.current = { key: col.key };

    const onMove = (ev) => {
      const next = Math.max(minW, startW + (ev.clientX - startX));
      setWidths((w) => ({ ...w, [col.key]: next }));
    };
    const onUp = () => {
      dragRef.current = null;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [widths]);

  // ---- sorting --------------------------------------------------------------
  const [internalSort, setInternalSort] = useState(null);
  const activeSort = sort !== undefined ? sort : internalSort;

  const toggleSort = useCallback((col) => {
    if (!col.sortable) return;
    const dir = activeSort?.key === col.key && activeSort?.direction === 'asc' ? 'desc' : 'asc';
    const next = { key: col.key, direction: dir };
    if (sort === undefined) setInternalSort(next);
    onSortChange?.(next);
  }, [activeSort, sort, onSortChange]);

  const setSortDir = useCallback((col, direction) => {
    const next = { key: col.key, direction };
    if (sort === undefined) setInternalSort(next);
    onSortChange?.(next);
  }, [sort, onSortChange]);

  const filterActiveSet = useMemo(
    () => (columnFilterActiveKeys instanceof Set ? columnFilterActiveKeys : new Set(columnFilterActiveKeys || [])),
    [columnFilterActiveKeys],
  );

  const rows = useMemo(() => {
    if (manualSort || !activeSort?.key) return data;
    const col = columns.find((c) => c.key === activeSort.key);
    if (!col) return data;
    const accessor = col.sortAccessor || ((row) => row[col.key]);
    const dir = activeSort.direction === 'desc' ? -1 : 1;
    return [...data].sort((a, b) => {
      const av = accessor(a);
      const bv = accessor(b);
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
  }, [data, activeSort, columns, manualSort]);

  // ---- selection ------------------------------------------------------------
  const selectedSet = useMemo(
    () => (selectedKeys instanceof Set ? selectedKeys : new Set(selectedKeys || [])),
    [selectedKeys],
  );
  const allKeys = rows.map((r, i) => rowKey(r, i));
  const allSelected = selectable && allKeys.length > 0 && allKeys.every((k) => selectedSet.has(k));
  const someSelected = selectable && allKeys.some((k) => selectedSet.has(k));

  const toggleAll = () => {
    if (!onSelectionChange) return;
    onSelectionChange(allSelected ? [] : allKeys);
  };
  const toggleRow = (key) => {
    if (!onSelectionChange) return;
    const next = new Set(selectedSet);
    if (next.has(key)) next.delete(key); else next.add(key);
    onSelectionChange(Array.from(next));
  };

  const colCount = columns.length + (selectable ? 1 : 0);

  return (
    <div className={`ps-table-wrap ps-table--${density} ${className}`.trim()}>
      <table className={`ps-table ${stickyHeader ? 'ps-table--sticky' : ''}`.trim()}>
        <colgroup>
          {selectable && <col style={{ width: 44 }} />}
          {columns.map((c) => <col key={c.key} style={{ width: widths[c.key] }} />)}
        </colgroup>
        <thead>
          <tr>
            {selectable && (
              <th className="ps-table__th ps-table__th--check">
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected && !allSelected}
                  onChange={toggleAll}
                  aria-label={t('Select all rows')}
                />
              </th>
            )}
            {columns.map((col) => {
              const isSorted = activeSort?.key === col.key;
              const resizable = col.resizable !== false;
              const hasMenu = typeof renderColumnFilter === 'function' && col.filterable;
              const filterActive = filterActiveSet.has(col.key);
              return (
                <th
                  key={col.key}
                  className={`ps-table__th ps-table__th--${col.align || 'left'} ${col.sortable ? 'is-sortable' : ''} ${isSorted ? 'is-sorted' : ''}`.trim()}
                  aria-sort={isSorted ? (activeSort.direction === 'asc' ? 'ascending' : 'descending') : undefined}
                >
                  <span
                    className="ps-table__th-inner"
                    onClick={() => toggleSort(col)}
                    role={col.sortable ? 'button' : undefined}
                    tabIndex={col.sortable ? 0 : undefined}
                    onKeyDown={(e) => { if (col.sortable && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); toggleSort(col); } }}
                  >
                    <span className="ps-table__th-label">{col.header}</span>
                    {col.sortable && (
                      <span className="ps-table__sort">
                        {isSorted
                          ? (activeSort.direction === 'asc' ? <Icon.ArrowUp size={14} /> : <Icon.ArrowDown size={14} />)
                          : <Icon.ChevronsUpDown size={14} />}
                      </span>
                    )}
                    {hasMenu && (
                      <span className="ps-table__colmenu" onClick={(e) => e.stopPropagation()}>
                        <Popover
                          placement="bottom-end"
                          panelClassName="ps-popover__panel--filter"
                          trigger={
                            <IconButton
                              aria-label={typeof col.header === 'string' ? t('Filter {column}', { column: col.header }) : t('Filter column')}
                              variant="ghost"
                              size="icon-sm"
                              className={`ps-table__filter-btn${filterActive ? ' is-active' : ''}`}
                            >
                              <Icon.ListFilter size={14} />
                            </IconButton>
                          }
                        >
                          {({ close }) => (
                            <div className="ps-colmenu">
                              {col.sortable && (
                                <div className="ps-colmenu__sort">
                                  <button type="button" className="ps-colmenu__sort-btn" onClick={() => { setSortDir(col, 'asc'); }}>
                                    <Icon.ArrowUp size={14} /> {t('Sort ascending')}
                                  </button>
                                  <button type="button" className="ps-colmenu__sort-btn" onClick={() => { setSortDir(col, 'desc'); }}>
                                    <Icon.ArrowDown size={14} /> {t('Sort descending')}
                                  </button>
                                </div>
                              )}
                              <div className="ps-colmenu__filter">
                                {renderColumnFilter(col, { close })}
                              </div>
                            </div>
                          )}
                        </Popover>
                      </span>
                    )}
                  </span>
                  {resizable && (
                    <span
                      className="ps-table__resize"
                      role="separator"
                      aria-orientation="vertical"
                      onMouseDown={(e) => onResizeStart(e, col)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td className="ps-table__empty" colSpan={colCount}>
                {emptyState || t('No data')}
              </td>
            </tr>
          ) : (
            rows.map((row, i) => {
              const key = rowKey(row, i);
              const isSelected = selectedSet.has(key);
              const extraRowClass = typeof rowClassName === 'function' ? (rowClassName(row, i) || '') : (rowClassName || '');
              return (
                <tr
                  key={key}
                  className={`ps-table__row ${isSelected ? 'is-selected' : ''} ${onRowClick ? 'is-clickable' : ''} ${extraRowClass}`.trim()}
                  onClick={onRowClick ? () => onRowClick(row, i) : undefined}
                >
                  {selectable && (
                    <td className="ps-table__td ps-table__td--check" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={isSelected}
                        onChange={() => toggleRow(key)}
                        aria-label={t('Select row')}
                      />
                    </td>
                  )}
                  {columns.map((col) => (
                    <td key={col.key} className={`ps-table__td ps-table__td--${col.align || 'left'}`}>
                      {col.render ? col.render(row, i) : row[col.key]}
                    </td>
                  ))}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
};

export default DataTable;
